import { useTranslation } from 'react-i18next';

import type { DashboardStats } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §5.1 — "**translation completeness** per content
 * type (a bar chart — this is the single best lever on the current site's
 * English gap)".
 *
 * ## Why this shape
 *
 * Each row is one ratio against a fixed limit, which is a **meter** rather than
 * a bar in a shared scale: 100% means the same thing on every row, so the track
 * is drawn and the fill sits inside it. One hue from one ramp — navy 600 on a
 * navy 100 track — because there is one series and its job is magnitude, not
 * identity. A single series needs no legend; the heading says what is plotted.
 *
 * Deliberately *not* coloured by threshold. Red-amber-green would encode the
 * same magnitude the bar length already carries, and would import a judgment
 * §5.1 does not make — nobody has said 60% is "warning". Status hues are kept
 * for status.
 *
 * Sorted worst first. The chart is a work list, and the row an editor should
 * act on is the one at the top.
 *
 * ## Why the numbers are text
 *
 * Every value is beside its bar and every count is spelled out, so this reads
 * as a table without one and needs no hover to be useful — which also means it
 * works on the tablet §6.8 supports and in a screen reader.
 */
export function TranslationChart({ rows }: { rows: DashboardStats['translation'] }) {
  const { t } = useTranslation();

  // A type with no records has nothing to translate and would sit at the top
  // of a worst-first list forever, which is the wrong thing to point an editor
  // at on their first morning.
  const drawn = [...rows]
    .filter((row) => row.total > 0)
    .sort((a, b) => a.completeness - b.completeness);

  if (drawn.length === 0) {
    return null;
  }

  return (
    <section
      aria-labelledby="translation-heading"
      className="rounded-card border border-subtle p-5"
    >
      <h2 id="translation-heading" className="text-h4 font-extrabold text-primary">
        {t('dashboard.translation.title')}
      </h2>
      <p className="mt-1 max-w-prose text-body-sm text-secondary">
        {t('dashboard.translation.intro')}
      </p>

      <ul className="mt-5 space-y-4">
        {drawn.map((row) => {
          const percent = Math.round(row.completeness * 100);

          return (
            <li key={row.type} className="grid grid-cols-[9rem_1fr_3rem] items-center gap-3">
              <span
                id={`translation-${row.type}`}
                className="truncate text-body-sm font-semibold text-primary"
              >
                {t(`dashboard.translation.types.${row.type}`, row.type)}
              </span>

              {/*
                `role="meter"`, which is what this is: a value inside a known
                range. The bar is 12px of a 16px band — capped rather than
                filling the slot — with the fill square where it starts and
                rounded at the data end.
              */}
              <span
                role="meter"
                // A meter needs a name as well as a value — `aria-valuetext`
                // alone fails `aria-meter-name`, and a bar announced as "75
                // percent" of nothing in particular is the failure the rule is
                // about. The name is the row's own label, so it is referenced
                // rather than repeated.
                aria-labelledby={`translation-${row.type}`}
                aria-valuenow={percent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuetext={t('dashboard.translation.rowLabel', {
                  type: t(`dashboard.translation.types.${row.type}`, row.type),
                  percent,
                  complete: row.complete,
                  total: row.total,
                })}
                className="block h-3 w-full overflow-hidden rounded-e-full bg-navy-100"
              >
                <span
                  className="block h-full rounded-e-full bg-navy-600"
                  style={{ width: `${Math.max(percent, percent > 0 ? 2 : 0)}%` }}
                />
              </span>

              {/* At the row end rather than inside the fill: a two-digit
                  percentage does not fit inside a 4%-wide bar, and a label its
                  own mark would clip is worse than one set beside it. */}
              <span className="text-end text-body-sm tabular-nums text-primary">{`${percent}%`}</span>

              <span className="col-start-2 col-end-4 -mt-2 text-caption text-muted">
                {t('dashboard.translation.count', { complete: row.complete, total: row.total })}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
